import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import DownloadIcon from "@mui/icons-material/Download";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import axios from "axios";
import { useAuth } from "contexts/AuthProvider";
import usePageTitle from "hooks/usePageTitle";
import Notification from "messages/Notification";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL;
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL;

const Verification = () => {
  const navigate = useNavigate();
  const { user, token, setUser } = useAuth();
  const [error, setError] = useState("");
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isInitialCheckLoading, setIsInitialCheckLoading] = useState(true);
  const queryParams = new URLSearchParams(window.location.search);
  const subscriptionSuccess =
    queryParams.get("subscription_success") === "true";
  const subscriptionCancel = queryParams.get("subscription_cancel") === "true";
  usePageTitle("Verify Your Identity");

  const [platform, setPlatform] = useState("other");

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const ua =
      window.navigator.userAgent ||
      (window.navigator as any).vendor ||
      (window as any).opera;

    if (/android/i.test(ua)) {
      setPlatform("android");
    } else if (/iPad|iPhone|iPod/.test(ua)) {
      setPlatform("ios");
    } else {
      setPlatform("other");
    }
  }, []);

  useEffect(() => {
    const checkInitialStatus = async () => {
      setIsInitialCheckLoading(true);
      try {
        const [subscriptionResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/subscription/status`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setHasSubscription(subscriptionResponse.data.is_subscribed);

        if (subscriptionResponse.data.hasSubscription) {
          navigate("/dashboard");
        }
      } catch (err) {
        setError("Failed to fetch status");
      } finally {
        setIsInitialCheckLoading(false);
      }
    };

    if (user && token) {
      checkInitialStatus();
    }
  }, [user, token, navigate]);

  useEffect(() => {
    const handleSubscriptionSuccess = async () => {
      if (subscriptionSuccess && token) {
        try {
          // Fetch updated user data
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/user`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          // Update local storage with new user data
          const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
          const updatedUser = {
            ...currentUser,
            ...response.data.user,
          };

          localStorage.setItem("user", JSON.stringify(updatedUser));
          setUser(updatedUser);

          // Show success message
          Notification("success", "Subscription activated successfully!");

          // Navigate to dashboard if both verification and subscription are complete
          if (
            response.data.user.identity_verified_at &&
            !response.data.needs_subscription
          ) {
            navigate("/subscription-success");
          }
        } catch (err) {
          setError("Failed to update subscription status");
        }
      }

      if (subscriptionCancel && token) {
        Notification(
          "error",
          "Subscription process cancelled. Please try again.",
        );
      }
    };

    handleSubscriptionSuccess();
  }, [subscriptionSuccess, token, navigate, setUser, subscriptionCancel]);

  const startSubscription = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/subscriptions`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      setError("Failed to start subscription process");
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{ my: 15, maxWidth: "600px", mx: "auto" }}
    >
      {error && Notification("error", error)}

      <Typography variant="h5" sx={{ mb: 4 }}>
        {hasSubscription ? "Setup Complete!" : "Complete Your Registration"}
      </Typography>

      <Box sx={{ width: "100%" }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, display: "flex", alignItems: "center" }}
        >
          <SubscriptionsIcon sx={{ mr: 1 }} />
          Step 2: Subscribe to Our Service (£4.99/month)
        </Typography>
        <Typography
          sx={{
            fontFamily: "Neue Haas Grotesk Display light",
            fontWeight: "450",
            marginBottom: "16px",
            textAlign: "left",
          }}
        >
          LMP Legal works with Stripe to manage subscriptions. Stripe builds
          technology that’s used by millions of companies around the world,
          including Amazon, Google, and Zoom. Stripe helps with everything from
          accepting payments to managing subscriptions to verifying identities.
          <br />
          <br />
          Learn more about how Stripe{" "}
          <a
            href="https://support.stripe.com/questions/common-questions-about-stripe-identity"
            target="_blank"
            rel="noopener noreferrer"
          >
            handles and stores your data
          </a>
          .
        </Typography>
        <Button
          onClick={startSubscription}
          variant="contained"
          // color="white"
          fullWidth
          size="large"
          disabled={isInitialCheckLoading}
          sx={{ color: "white" }}
        >
          {isInitialCheckLoading
            ? "Loading..."
            : hasSubscription
              ? "Subscription Active ✓"
              : "Start Subscription"}
        </Button>
      </Box>

      <Box sx={{ width: "100%", mt: 4 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, display: "flex", alignItems: "center" }}
        >
          <DownloadIcon sx={{ mr: 1 }} />
          Step 3: Download the App
        </Typography>

        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          {(platform === "ios" || platform === "other") && (
            <Button
              component="a"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="large"
              sx={{
                flex: 1,
                height: "56px",
                fontSize: "1rem",
              }}
              startIcon={<AppleIcon />}
            >
              App Store
            </Button>
          )}
          {(platform === "android" || platform === "other") && (
            <Button
              component="a"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="large"
              sx={{
                flex: 1,
                height: "56px",
                fontSize: "1rem",
              }}
              startIcon={<AndroidIcon />}
            >
              Play Store
            </Button>
          )}
        </Stack>

        <Typography
          variant="body2"
          color="white"
          sx={{ mt: 1, textAlign: "center" }}
        >
          {!hasSubscription
            ? "Complete verification and subscription to get full access to the app"
            : "You're all set! Download the app to get started"}
        </Typography>
      </Box>
    </Box>
  );
};

export default Verification;
